<template>
  <b-card class="sales-stage-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body :title="'Number of Leads vs Lead Stage'" role="button" tabindex="0">
    <template #header>
      <!-- <h6 class="mb-0">Sales by Stage</h6> -->
    </template>
    <b-card-body>
      <apexchart v-if="chartReady && safeSeries && safeSeries.length" :key="chartKey" type="bar" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'SalesStageChart',
  emits: ['chart-point-click'],
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'bar',
          height: 350,
          toolbar: {
            show: false,
          },
          events: {
            dataPointSelection: (e, ctx, config) => this.handleDataPointSelection(e, ctx, config),
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
          },
        },
        colors: ['#7367f0'],
        dataLabels: {
          enabled: false,
        },
        xaxis: {
          categories: [], // Stage names will go here
        },
        yaxis: {
          title: {
            text: 'Count',
          },
        },
      },
      series: [{
        name: 'Leads',
        data: [],
      }],
    };
  },
  computed: {
    safeSeries() {
      const s = (this.series || []).filter(Boolean);
      const mapped = s.map((item) => {
        const raw = item && typeof item === 'object' ? item : {};
        return {
          name: raw.name || 'Leads',
          data: (Array.isArray(raw.data) ? raw.data : []).map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0)),
        };
      });
      return mapped.length ? mapped : [{ name: 'Leads', data: [] }];
    },
    chartReady() {
      const chart = this.chartOptions && this.chartOptions.chart;
      return chart && typeof chart.height === 'number' && chart.height > 0;
    },
    chartKey() {
      const cats = (this.chartOptions && this.chartOptions.xaxis && this.chartOptions.xaxis.categories) || [];
      const first = (this.series && this.series[0]) || {};
      const vals = Array.isArray(first.data) ? first.data : [];
      return `sales-stage-${cats.join('|')}__${vals.join('|')}`;
    },
  },
  watch: {
    dashboardData: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val.sales_stage_chart != null && hasRight(this.user, 'dashboard', 'view')) {
          this.processChartData(this.normalizeToArray(val.sales_stage_chart));
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.sales_stage_chart != null) {
            this.processChartData(this.normalizeToArray(this.dashboardData.sales_stage_chart));
          } else {
            this.fetchSalesStageChartData();
          }
          return;
        }
        if (this.dashboardData && this.dashboardData.sales_stage_chart != null) {
          this.processChartData(this.normalizeToArray(this.dashboardData.sales_stage_chart));
          return;
        }
        this.fetchSalesStageChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.sales_stage_chart != null) this.processChartData(this.normalizeToArray(this.dashboardData.sales_stage_chart));
            return;
          }
          this.fetchSalesStageChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [{ name: 'Leads', data: [] }];
        }
      },
    },
  },
  mounted() {
    this.ensureChartData();
  },
  methods: {
    hasRight, // Make the helper available in the template
    normalizeToArray(val) {
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') return Object.values(val);
      return [];
    },
    ensureChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.dashboardData && this.dashboardData.sales_stage_chart != null) {
        this.processChartData(this.normalizeToArray(this.dashboardData.sales_stage_chart));
      } else {
        this.fetchSalesStageChartData();
      }
    },
    async fetchSalesStageChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate && this.dashboardData && this.dashboardData.sales_stage_chart != null) {
        this.processChartData(this.normalizeToArray(this.dashboardData.sales_stage_chart));
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/sales-stage-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(this.normalizeToArray(raw));
      } catch (error) {
        console.error('Error fetching sales stage chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || !Array.isArray(data)) {
        this.series = [{ name: 'Leads', data: [] }];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        return;
      }
      const rows = data.filter(item => item && typeof item === 'object');
      if (rows.length === 0) {
        this.series = [{ name: 'Leads', data: [] }];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        return;
      }
      const categories = rows.map(item => item.stage_name ?? item.stage ?? item.name ?? 'Unknown');
      const seriesData = rows.map(item => Number(item.count ?? item.total ?? item.value ?? 0) || 0);

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: categories,
        },
      };

      this.series = [{
        name: 'Leads',
        data: seriesData,
      }];
    },
    handleDataPointSelection(event, chartContext, config) {
      if (config.dataPointIndex !== undefined && this.chartOptions.xaxis.categories[config.dataPointIndex]) {
        const stageName = this.chartOptions.xaxis.categories[config.dataPointIndex];
        this.$emit('chart-point-click', 'Number of Leads vs Lead Stage', '/dashboard/crm/sales-stage-chart/detail/' + stageName, { stage_name: stageName });
      }
    },
  },
};
</script>

<style scoped>
.sales-stage-chart {
  margin-top: 20px;
  cursor: pointer; /* Added for clickable affordance */
}
</style>